import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  GraphNode,
  GraphEdge,
  NodeType
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import "./GraphCanvas.scss";

type GraphCanvasProps = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedNodeId: string | null;
  onNodeSelect: (nodeId: string) => void;
  // Node ids to emphasise with a distinct colour (e.g. affected systems).
  highlightedNodeIds?: string[];
  // When true, zoom scales the SVG's pixel size so the container scrolls
  // (instead of the default viewBox zoom which relies on drag-to-pan).
  scrollZoom?: boolean;
};

const HIGHLIGHT_COLOR = "#dc2626";

const NODE_RADIUS_BASE = 34;
const LINE_HEIGHT = 11;

// Splits a label into up to 3 centered lines that fit inside a circular node.
const wrapLabel = (label: string): string[] => {
  const text = (label ?? "").trim();
  if (!text) return [""];
  const words = text.split(/\s+/);
  const maxChars = 9;
  const lines: string[] = [];
  let current = "";
  const flush = () => {
    if (current) {
      lines.push(current);
      current = "";
    }
  };
  words.forEach((word) => {
    let w = word;
    while (w.length > maxChars) {
      flush();
      lines.push(w.slice(0, maxChars));
      w = w.slice(maxChars);
    }
    if (!current) current = w;
    else if ((current + " " + w).length <= maxChars) current += " " + w;
    else {
      flush();
      current = w;
    }
  });
  flush();
  return lines.slice(0, 3);
};

// Fruchterman–Reingold force-directed layout (dependency-free).
const computeForceLayout = (
  nodes: GraphNode[],
  edges: GraphEdge[]
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  const n = nodes.length;
  if (n === 0) return positions;
  const width = 1000;
  const height = 720;
  if (n === 1) {
    positions[nodes[0].id] = { x: width / 2, y: height / 2 };
    return positions;
  }
  const area = width * height;
  const k = Math.sqrt(area / n) * 0.75;
  nodes.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / n;
    positions[node.id] = {
      x: width / 2 + k * 1.5 * Math.cos(angle),
      y: height / 2 + k * 1.5 * Math.sin(angle)
    };
  });
  const iterations = 320;
  let temp = width / 8;
  for (let it = 0; it < iterations; it++) {
    const currentTemp = temp;
    const disp: Record<string, { x: number; y: number }> = {};
    nodes.forEach((node) => (disp[node.id] = { x: 0, y: 0 }));
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const na = nodes[a];
        const nb = nodes[b];
        const dx = positions[na.id].x - positions[nb.id].x;
        const dy = positions[na.id].y - positions[nb.id].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const force = (k * k) / dist;
        const ux = dx / dist;
        const uy = dy / dist;
        disp[na.id].x += ux * force;
        disp[na.id].y += uy * force;
        disp[nb.id].x -= ux * force;
        disp[nb.id].y -= uy * force;
      }
    }
    edges.forEach((edge) => {
      const s = positions[edge.source];
      const t = positions[edge.target];
      if (!s || !t) return;
      const dx = s.x - t.x;
      const dy = s.y - t.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const force = (dist * dist) / k;
      const ux = dx / dist;
      const uy = dy / dist;
      disp[edge.source].x -= ux * force;
      disp[edge.source].y -= uy * force;
      disp[edge.target].x += ux * force;
      disp[edge.target].y += uy * force;
    });
    nodes.forEach((node) => {
      const d = disp[node.id];
      const len = Math.sqrt(d.x * d.x + d.y * d.y) || 0.01;
      positions[node.id].x += (d.x / len) * Math.min(len, currentTemp);
      positions[node.id].y += (d.y / len) * Math.min(len, currentTemp);
      positions[node.id].x += (width / 2 - positions[node.id].x) * 0.008;
      positions[node.id].y += (height / 2 - positions[node.id].y) * 0.008;
    });
    temp *= 0.985;
  }
  const pad = 90;
  const xs = nodes.map((node) => positions[node.id].x);
  const ys = nodes.map((node) => positions[node.id].y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  nodes.forEach((node) => {
    positions[node.id].x += pad - minX;
    positions[node.id].y += pad - minY;
  });
  return positions;
};

const getNodeColor = (type: NodeType): string => {
  switch (type) {
    case NodeType.ASSET:
      return "#7c3aed";
    case NodeType.SYSTEM:
      return "#06b6d4";
    case NodeType.SUBSYSTEM:
      return "#10b981";
    case NodeType.EQUIPMENT:
      return "#f59e0b";
    case NodeType.REQUIREMENT:
      return "#ef4444";
    case NodeType.DISCIPLINE:
      return "#ec4899";
    case NodeType.DOCUMENT:
      return "#3b82f6";
    case NodeType.COMPONENT:
      return "#f59e0b";
    case NodeType.SENSOR:
      return "#06b6d4";
    default:
      return "#6b7280";
  }
};

const GraphCanvas: FC<GraphCanvasProps> = ({
  nodes,
  edges,
  selectedNodeId,
  onNodeSelect,
  highlightedNodeIds,
  scrollZoom
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const highlightSet = useMemo(
    () => new Set(highlightedNodeIds ?? []),
    [highlightedNodeIds]
  );
  const [positions, setPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    nodeId: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    origPanX: number;
    origPanY: number;
  } | null>(null);

  const layout = useMemo(
    () => computeForceLayout(nodes, edges),
    [nodes, edges]
  );

  // Larger radius for more connected (hub) nodes.
  const radiusMap = useMemo(() => {
    const degree: Record<string, number> = {};
    nodes.forEach((node) => (degree[node.id] = 0));
    edges.forEach((edge) => {
      if (degree[edge.source] !== undefined) degree[edge.source] += 1;
      if (degree[edge.target] !== undefined) degree[edge.target] += 1;
    });
    const map: Record<string, number> = {};
    nodes.forEach((node) => {
      map[node.id] = NODE_RADIUS_BASE + Math.min(degree[node.id] ?? 0, 8) * 2.5;
    });
    return map;
  }, [nodes, edges]);

  useEffect(() => {
    setPositions(layout);
  }, [layout]);

  // In scroll-zoom mode, track the container size so the graph fits at zoom 1
  // and only overflows (scrolls) when the user zooms in.
  useEffect(() => {
    if (!scrollZoom) {
      return;
    }
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const update = () =>
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollZoom]);

  // Keep the graph centred in the viewport on load and while zooming.
  useLayoutEffect(() => {
    if (!scrollZoom) {
      return;
    }
    const vp = viewportRef.current;
    if (!vp) {
      return;
    }
    vp.scrollLeft = Math.max(0, (vp.scrollWidth - vp.clientWidth) / 2);
    vp.scrollTop = Math.max(0, (vp.scrollHeight - vp.clientHeight) / 2);
  }, [zoom, containerSize, layout, scrollZoom]);

  const getRadius = (id: string) => radiusMap[id] ?? NODE_RADIUS_BASE;

  const getSVGPoint = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: clientX, y: clientY };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: clientX, y: clientY };
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.preventDefault();
      e.stopPropagation();
      const svgPt = getSVGPoint(e.clientX, e.clientY);
      const pos = positions[nodeId];
      if (!pos) return;
      dragRef.current = {
        nodeId,
        startX: svgPt.x,
        startY: svgPt.y,
        origX: pos.x,
        origY: pos.y
      };
    },
    [getSVGPoint, positions]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragRef.current) {
        const svgPt = getSVGPoint(e.clientX, e.clientY);
        const { nodeId, startX, startY, origX, origY } = dragRef.current;
        const dx = svgPt.x - startX;
        const dy = svgPt.y - startY;
        setPositions((prev) => ({
          ...prev,
          [nodeId]: { x: origX + dx, y: origY + dy }
        }));
      } else if (panRef.current) {
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        const scale = 1 / zoom;
        setPan({
          x: panRef.current.origPanX + dx * scale,
          y: panRef.current.origPanY + dy * scale
        });
      }
    },
    [getSVGPoint, zoom]
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
    panRef.current = null;
  }, []);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // In scroll-zoom mode the scrollbars handle navigation, not drag-pan.
      if (scrollZoom) {
        return;
      }
      // Only pan if clicking on the SVG background (not a node)
      if (
        (e.target as Element).tagName === "svg" ||
        (e.target as Element).classList.contains("graph-canvas__bg")
      ) {
        panRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          origPanX: pan.x,
          origPanY: pan.y
        };
      }
    },
    [pan, scrollZoom]
  );

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.3));
  const handleFit = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const getPos = (id: string) => positions[id] || { x: 0, y: 0 };

  // Compute dynamic viewBox based on node positions
  const posValues = Object.values(positions);
  const maxX =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.x)) + 140 : 1200;
  const maxY =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.y)) + 140 : 800;
  const vbWidth = Math.max(1200, maxX) / zoom;
  const vbHeight = Math.max(800, maxY) / zoom;
  const contentWidth = Math.max(1200, maxX);
  const contentHeight = Math.max(800, maxY);
  // Scroll mode uses a tight bounding box around the nodes so the graph fills
  // the card (less empty space), then grows a sizer wrapper with zoom.
  const bboxPad = 70;
  const minPx =
    posValues.length > 0 ? Math.min(...posValues.map((p) => p.x)) : 0;
  const minPy =
    posValues.length > 0 ? Math.min(...posValues.map((p) => p.y)) : 0;
  const maxPx =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.x)) : 1200;
  const maxPy =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.y)) : 800;
  const bboxW = maxPx - minPx + bboxPad * 2;
  const bboxH = maxPy - minPy + bboxPad * 2;
  const viewBox = scrollZoom
    ? `${minPx - bboxPad} ${minPy - bboxPad} ${bboxW} ${bboxH}`
    : `${-pan.x} ${-pan.y} ${vbWidth} ${vbHeight}`;
  const sizerStyle: React.CSSProperties | undefined = scrollZoom
    ? {
        width: (containerSize.w || contentWidth) * zoom,
        height: (containerSize.h || contentHeight) * zoom
      }
    : undefined;

  return (
    <div className="graph-canvas" ref={containerRef}>
      <div
        ref={viewportRef}
        className={`graph-canvas__viewport${
          scrollZoom ? " graph-canvas__viewport--scroll" : ""
        }`}
      >
        <div
          className={`graph-canvas__sizer${
            scrollZoom ? " graph-canvas__sizer--scroll" : ""
          }`}
          style={sizerStyle}
        >
          <svg
            ref={svgRef}
            className="graph-canvas__svg"
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="5"
                refY="5"
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 Z" fill="#6b7280" />
              </marker>
            </defs>

            <g>
              {/* Invisible background for pan detection */}
              <rect
                className="graph-canvas__bg"
                x={-pan.x}
                y={-pan.y}
                width={5000}
                height={5000}
                fill="transparent"
              />
              {/* Edges */}
              {edges.map((edge) => {
                const source = getPos(edge.source);
                const target = getPos(edge.target);
                const sr = getRadius(edge.source);
                const tr = getRadius(edge.target);
                const dx = target.x - source.x;
                const dy = target.y - source.y;
                const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
                const ux = dx / dist;
                const uy = dy / dist;
                // Clip line to the circle boundaries; leave room for the arrow.
                const x1 = source.x + ux * sr;
                const y1 = source.y + uy * sr;
                const x2 = target.x - ux * (tr + 9);
                const y2 = target.y - uy * (tr + 9);
                const midX = (x1 + x2) / 2;
                const midY = (y1 + y2) / 2;
                const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                return (
                  <g key={edge.id}>
                    <line
                      className="graph-canvas__edge"
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                    />
                    <polygon
                      className="graph-canvas__arrow"
                      points="-9,-5 0,0 -9,5"
                      transform={`translate(${x2}, ${y2}) rotate(${angle})`}
                    />
                    {edge.relationship && (
                      <text
                        className="graph-canvas__edge-label"
                        x={midX}
                        y={midY - 4}
                        textAnchor="middle"
                      >
                        {edge.relationship}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Nodes */}
              {nodes.map((node) => {
                const isHighlighted = highlightSet.has(node.id);
                const color = isHighlighted
                  ? HIGHLIGHT_COLOR
                  : getNodeColor(node.type);
                const isSelected = selectedNodeId === node.id;
                const pos = getPos(node.id);
                const r = getRadius(node.id);
                const lines = wrapLabel(node.label);
                const startY = -((lines.length - 1) * LINE_HEIGHT) / 2;

                return (
                  <g
                    key={node.id}
                    className={`graph-node${
                      isHighlighted ? " graph-node--highlighted" : ""
                    }`}
                    transform={`translate(${pos.x}, ${pos.y})`}
                    onMouseDown={(e) => handleMouseDown(e, node.id)}
                    onClick={() => onNodeSelect(node.id)}
                  >
                    {isSelected && (
                      <circle
                        className="graph-node__selection"
                        cx={0}
                        cy={0}
                        r={r + 5}
                      />
                    )}
                    {isHighlighted && (
                      <circle
                        className="graph-node__highlight-ring"
                        cx={0}
                        cy={0}
                        r={r + 4}
                        fill="none"
                        stroke={HIGHLIGHT_COLOR}
                        strokeWidth={3}
                      />
                    )}
                    <circle
                      className="graph-node__circle"
                      cx={0}
                      cy={0}
                      r={r}
                      fill={color}
                    />
                    <text className="graph-node__label" textAnchor="middle">
                      {lines.map((line, i) => (
                        <tspan key={i} x={0} y={startY + i * LINE_HEIGHT + 3}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="graph-canvas__controls">
        <button
          type="button"
          className="graph-canvas__control-btn"
          onClick={handleZoomIn}
          title="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className="graph-canvas__control-btn"
          onClick={handleZoomOut}
          title="Zoom out"
        >
          &minus;
        </button>
        {/* <button
          className="graph-canvas__control-btn"
          onClick={handleFit}
          title="Fit to view"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button> */}
      </div>
    </div>
  );
};

export default GraphCanvas;
